import { Check, Sparkles, Trophy } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useTranslation } from '../context/TranslationContext';

// Shared plan card for /pricing (PricingPage.js) and the home page's #pricing
// section (LandingPage.js), so both always show the same plans, prices and
// features. Purely presentational: the parent owns plan data (GET
// /settings/plans), the selected plan, any coupon-adjusted numbers, and
// whatever goes below the feature list (payment buttons vs. a CTA).

export const PLAN_INCLUDES = [
  { key: 'pricing_inc_all_courses', fb: 'All current & future courses' },
  { key: 'pricing_inc_offline', fb: 'Offline downloads' },
  { key: 'pricing_inc_certs', fb: 'Certificates' },
  { key: 'pricing_inc_mobile', fb: 'Mobile app' },
  { key: 'pricing_inc_community', fb: 'Community access' },
  { key: 'pricing_inc_support', fb: 'Priority support' },
];

// India formats with Intl's INR formatter; every other country prefixes its
// currency_symbol (admin-entered currencies have no reliable Intl mapping).
export function formatPlanPrice(price, { isIndia, symbol = '' }) {
  if (isIndia) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }
  return `${symbol}${Number(price).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

// Default tab: Annual if offered, else Lifetime, else the first plan.
export function defaultPlanId(plans, preferred) {
  if (preferred && plans.some((p) => p.id === preferred)) return preferred;
  return (plans.find((p) => p.id === 'annual') || plans.find((p) => p.id === 'lifetime') || plans[0])?.id || 'lifetime';
}

export default function PricingPlanCard({
  plans = [],
  selectedPlanId,
  onSelectPlan,
  lifetimeBadge = 'BEST VALUE',
  headerExtra = null,
  price,
  originalPrice,
  taxRate,
  taxLabel,
  total,
  isIndia,
  formatPrice,
  beforeFeatures = null,
  children,
  className = '',
}) {
  const { t, tFormat } = useTranslation();
  const datedPlans = plans.filter((p) => p.id !== 'lifetime');
  const selectedPlan = plans.find((p) => p.id === selectedPlanId) || null;
  const isDatedPlan = !!selectedPlan && selectedPlan.id !== 'lifetime';

  return (
    <Card className={`relative overflow-hidden border-2 border-[#A67C52] shadow-lg ${className}`} data-testid="lifetime-plan-card">
      <div className="absolute top-0 right-0 bg-[#A67C52] text-white px-3 py-1 text-xs font-bold uppercase tracking-wide rounded-bl-lg flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> {isDatedPlan ? (selectedPlan.badge || selectedPlan.name) : lifetimeBadge}
      </div>

      <CardContent className="pt-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Trophy className="w-8 h-8 text-[#A67C52]" />
            <h3 className="text-2xl font-bold">
              {isDatedPlan
                ? tFormat('pricing_plan_title', { name: selectedPlan.name }, '{name} Plan')
                : t('pricing_lifetime_title', 'Premium Access')}
            </h3>
          </div>
          {headerExtra}
        </div>

        {datedPlans.length > 0 && (
          <div
            className="grid grid-cols-3 sm:grid-cols-5 gap-1 p-1 mb-5 rounded-xl bg-muted"
            role="tablist"
            aria-label={t('pricing_plan_tabs', 'Choose a plan')}
            data-testid="plan-tabs"
          >
            {plans.map((plan) => {
              const active = plan.id === selectedPlanId;
              return (
                <button
                  key={plan.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => onSelectPlan(plan.id)}
                  className={`rounded-lg px-1.5 py-2 text-xs leading-tight transition-colors ${
                    active ? 'bg-[#A67C52] text-white font-bold shadow' : 'text-foreground/80 hover:bg-background'
                  }`}
                  data-testid={`plan-tab-${plan.id}`}
                >
                  {plan.id === 'lifetime' ? t('pricing_tab_lifetime', 'Lifetime') : plan.name}
                  {plan.savings_percent > 0 && (
                    <span className={`block text-[10px] ${active ? 'opacity-90' : 'opacity-60'}`}>
                      {tFormat('pricing_tab_save', { pct: plan.savings_percent }, 'Save {pct}%')}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        {isDatedPlan && selectedPlan.description && (
          <p className="text-sm text-muted-foreground mb-3">{selectedPlan.description}</p>
        )}

        <div className="mb-1">
          {originalPrice > price && (
            <div className="text-lg text-muted-foreground line-through">{formatPrice(originalPrice)}</div>
          )}
          <span className="text-5xl font-bold text-[#A67C52]">{formatPrice(price)}</span>
          {isDatedPlan && (
            <span className="ml-2 text-sm text-muted-foreground">
              {tFormat('pricing_for_days', { days: selectedPlan.duration_days }, 'for {days} days')}
            </span>
          )}
        </div>
        {isDatedPlan && selectedPlan.duration_months > 1 && (
          <p className="text-sm text-muted-foreground mb-0.5">
            {tFormat('pricing_per_month', { price: formatPrice(selectedPlan.monthly_equivalent) }, '≈ {price}/month')}
          </p>
        )}
        <p className="text-sm text-muted-foreground mb-0.5">
          {isIndia
            ? tFormat('pricing_gst_line', { pct: taxRate }, '+ {pct}% GST')
            : `+ ${taxRate}% ${taxLabel}`}
        </p>
        <p className="text-xs text-muted-foreground mb-8">
          {isIndia
            ? tFormat('pricing_gst_inclusive_line', { total: formatPrice(total) }, '({total} inclusive)')
            : `(${formatPrice(total)} inclusive)`}
        </p>

        {beforeFeatures}

        <ul className="space-y-2 mb-8">
          {PLAN_INCLUDES.map((feature) => (
            <li key={feature.key} className="flex items-center gap-2 text-sm">
              <Check className="w-4 h-4 text-green-500 shrink-0" />
              <span>{t(feature.key, feature.fb)}</span>
            </li>
          ))}
        </ul>

        {children}
      </CardContent>
    </Card>
  );
}
